import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import { hrAPI } from '../../../api/hr';
import { DEFAULT_PAGINATION, MONTH_OPTIONS, PAYROLL_STATUSES, YEAR_OPTIONS } from '../constants';
import PayrollFormModal from './PayrollFormModal';

const PAGE_SIZE = 10;

const statusTone = {
  Pending: 'bg-amber-50 text-amber-700',
  Processed: 'bg-blue-50 text-blue-700',
  Paid: 'bg-green-50 text-green-700',
  Failed: 'bg-red-50 text-red-700',
  Cancelled: 'bg-gray-100 text-gray-600'
};

const formatMoney = (value, currency = 'INR') => {
  if (value === undefined || value === null) return '-';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const PayrollTab = ({
  employeeOptions = [],
  employeesLoading = false,
  showSuccess,
  showError,
  onDataChanged
}) => {
  const [filters, setFilters] = useState({ user: '', paymentStatus: '', month: '', year: '', page: 1 });
  const [records, setRecords] = useState([]);
  const [pagination, setPagination] = useState({ ...DEFAULT_PAGINATION, limit: PAGE_SIZE });
  const [loading, setLoading] = useState(true);
  const [modalState, setModalState] = useState({ open: false, mode: 'create', record: null });
  const [deletingId, setDeletingId] = useState(null);
  const [salaryOptions, setSalaryOptions] = useState([]);
  const [salariesLoading, setSalariesLoading] = useState(false);

  const fetchPayrolls = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: filters.page,
        limit: PAGE_SIZE,
        user: filters.user || undefined,
        paymentStatus: filters.paymentStatus || undefined,
        month: filters.month || undefined,
        year: filters.year || undefined
      };
      const response = await hrAPI.listPayrolls(params);
      setRecords(response?.items || []);
      setPagination({
        ...DEFAULT_PAGINATION,
        limit: PAGE_SIZE,
        ...(response?.pagination || {})
      });
    } catch (error) {
      showError?.(error.message || 'Failed to load payroll records');
    } finally {
      setLoading(false);
    }
  }, [filters, showError]);

  useEffect(() => {
    fetchPayrolls();
  }, [fetchPayrolls]);

  const totalPages = useMemo(() => Math.max(pagination.pages || 1, 1), [pagination.pages]);

  const updateFilter = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value, page: 1 }));
  };

  const handlePageChange = (direction) => {
    setFilters((prev) => ({
      ...prev,
      page: Math.min(Math.max(prev.page + direction, 1), totalPages)
    }));
  };

  const loadSalaryOptions = useCallback(async () => {
    try {
      setSalariesLoading(true);
      const response = await hrAPI.listSalaries({ isActive: true, limit: 100 });
      const items = response?.items || [];
      const mapped = items.map((item) => ({
        value: item._id,
        label: `${item.user ? `${item.user.firstName || ''} ${item.user.lastName || ''}`.trim() || item.user.email : 'Salary'} · ${formatMoney(item.netSalary ?? item.basicSalary, item.currency)} · ${item.paymentFrequency}`,
        user: item.user?._id || item.user,
        paymentFrequency: item.paymentFrequency,
        currency: item.currency
      }));
      setSalaryOptions(mapped);
    } catch (error) {
      showError?.('Unable to load salary options');
    } finally {
      setSalariesLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadSalaryOptions();
  }, [loadSalaryOptions]);

  const handleCreate = async (payload) => {
    try {
      await hrAPI.createPayroll(payload);
      showSuccess?.('Payroll generated');
      await fetchPayrolls();
      onDataChanged?.();
      return true;
    } catch (error) {
      showError?.(error.message || 'Unable to create payroll');
      return false;
    }
  };

  const handleUpdate = async (payload) => {
    if (!modalState.record?._id) return false;
    try {
      await hrAPI.updatePayroll(modalState.record._id, payload);
      showSuccess?.('Payroll updated');
      await fetchPayrolls();
      onDataChanged?.();
      return true;
    } catch (error) {
      showError?.(error.message || 'Unable to update payroll');
      return false;
    }
  };

  const handleDelete = async (record) => {
    if (!record?._id) return;
    if (!window.confirm('Delete this payroll entry?')) {
      return;
    }
    try {
      setDeletingId(record._id);
      await hrAPI.deletePayroll(record._id);
      showSuccess?.('Payroll record deleted');
      await fetchPayrolls();
      onDataChanged?.();
    } catch (error) {
      showError?.(error.message || 'Unable to delete payroll');
    } finally {
      setDeletingId(null);
    }
  };

  const currentModalSubmit = modalState.mode === 'edit' ? handleUpdate : handleCreate;

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Payroll Processing</h3>
          <p className="text-sm text-gray-500">Generate and reconcile monthly payroll runs</p>
        </div>
        <button
          onClick={() => setModalState({ open: true, mode: 'create', record: null })}
          className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Icon name="FilePlus" size={16} className="mr-2" />
          Generate Payroll
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Employee</label>
            <select
              value={filters.user}
              onChange={(event) => updateFilter('user', event.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">
                {employeesLoading ? 'Loading employees...' : 'All employees'}
              </option>
              {employeeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Status</label>
            <select
              value={filters.paymentStatus}
              onChange={(event) => updateFilter('paymentStatus', event.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">All</option>
              {PAYROLL_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Month</label>
            <select
              value={filters.month}
              onChange={(event) => updateFilter('month', event.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">All</option>
              {MONTH_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Year</label>
            <select
              value={filters.year}
              onChange={(event) => updateFilter('year', event.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">All</option>
              {YEAR_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex items-center justify-between mt-4">
          <span className="text-xs text-gray-500">Page {pagination.page || 1} of {totalPages}</span>
          <button
            onClick={() => setFilters({ user: '', paymentStatus: '', month: '', year: '', page: 1 })}
            className="text-sm text-blue-600 hover:underline"
          >
            Reset filters
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500">Employee</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500">Period</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500">Net Salary</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500">Attendance</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500">Payment</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500">Generated By</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-16 text-center text-gray-500">
                    <div className="flex items-center justify-center space-x-2">
                      <Icon name="Loader2" size={20} className="animate-spin text-blue-600" />
                      <span>Loading payroll data...</span>
                    </div>
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-16 text-center text-gray-500">
                    No payroll entries yet
                  </td>
                </tr>
              ) : (
                records.map((record) => {
                  const currency = record.salary?.currency || record.currency || 'USD';
                  return (
                    <tr key={record._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="font-medium">{record.user ? `${record.user.firstName || ''} ${record.user.lastName || ''}`.trim() || record.user.email : '—'}</div>
                      <div className="text-xs text-gray-500">{record.user?.role}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {record.period || `${MONTH_OPTIONS.find((opt) => opt.value === record.month)?.label || record.month} ${record.year}`}
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                      {formatMoney(record.netSalary, currency)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div>Present: {record.presentDays}</div>
                      <div className="text-xs text-gray-500">Absent: {record.absentDays} · Leave: {record.leaveDays}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusTone[record.paymentStatus] || 'bg-gray-100 text-gray-600'}`}>
                        {record.paymentStatus}
                      </span>
                      <div className="text-xs text-gray-500">{record.paymentMethod || 'Unassigned'}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div>{record.generatedBy ? `${record.generatedBy.firstName || ''} ${record.generatedBy.lastName || ''}`.trim() || record.generatedBy.email : '-'}</div>
                      <div className="text-xs text-gray-500">{record.createdAt ? new Date(record.createdAt).toLocaleDateString() : '-'}</div>
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-3">
                        <button
                          onClick={() => setModalState({ open: true, mode: 'edit', record })}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <Icon name="BadgeCheck" size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(record)}
                          disabled={deletingId === record._id}
                          className="text-red-600 hover:text-red-800 disabled:opacity-50"
                        >
                          {deletingId === record._id ? <Icon name="Loader2" size={16} className="animate-spin" /> : <Icon name="Trash2" size={16} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-gray-600">
          <span>
            Showing {(pagination.page - 1) * PAGE_SIZE + 1} - {Math.min(pagination.page * PAGE_SIZE, pagination.total || records.length)} of {pagination.total || records.length}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(-1)}
              disabled={filters.page <= 1}
              className="p-2 rounded-lg border border-gray-300 text-gray-600 disabled:opacity-40"
            >
              <Icon name="ChevronLeft" size={16} />
            </button>
            <span>Page {filters.page} / {totalPages}</span>
            <button
              onClick={() => handlePageChange(1)}
              disabled={filters.page >= totalPages}
              className="p-2 rounded-lg border border-gray-300 text-gray-600 disabled:opacity-40"
            >
              <Icon name="ChevronRight" size={16} />
            </button>
          </div>
        </div>
      </div>

      <PayrollFormModal
        open={modalState.open}
        mode={modalState.mode}
        initialData={modalState.record}
        employeeOptions={employeeOptions}
        salaryOptions={salaryOptions}
        employeesLoading={employeesLoading}
        salariesLoading={salariesLoading}
        onClose={() => setModalState({ open: false, mode: 'create', record: null })}
        onSubmit={currentModalSubmit}
      />
    </div>
  );
};

export default PayrollTab;
